import { REGIONS } from "./regions";
import { hashNoise } from "./math";
import { animationFrame } from "./animation";
import type { Animated, Enemy, Vec2 } from "./types";
import type { World } from "./world";

const BOSS_CELL = 256;
const BOSS_GROUND = 238;

/** 远征只负责表现自己的场景；绝不从背景图片推导碰撞或修改家园状态。 */
export class ExpeditionPainter {
  private terrain: HTMLImageElement | null = null;
  private bosses = new Map<string, HTMLImageElement>();

  private load(url: string): HTMLImageElement {
    const image = new Image();
    image.decoding = "async";
    image.src = url;
    return image;
  }

  terrainLayer(
    ctx: CanvasRenderingContext2D,
    run: NonNullable<World["run"]>,
    time: number,
    camera: Vec2,
  ): void {
    // 家园首屏不下载远征底图；首次跨门后复用已缓存的压缩资源。
    const terrain = (this.terrain ??= this.load(
      "/art/expedition-terrain-v5.webp",
    ));
    const ember = run.mapId === "ember-quarry";
    const accent = ember ? "#ffca75" : "#a0ebcc";
    for (const region of REGIONS) {
      if (Math.abs(region.x - camera.x) > 1550) continue;
      ctx.save();
      const cliff = ctx.createLinearGradient(0, region.y, 0, region.y + 450);
      cliff.addColorStop(0, ember ? "#66513f" : "#315d5c");
      cliff.addColorStop(1, "#132e3b");
      ctx.fillStyle = cliff;
      ctx.beginPath();
      ctx.ellipse(
        region.x,
        region.y + 90,
        region.rx,
        region.ry + 8,
        0,
        0,
        Math.PI * 2,
      );
      ctx.fill();
      // 细岩层与垂直根系给浮岛厚度，地表轮廓始终与可走椭圆一致。
      for (let i = 0; i < 23; i++) {
        const a = (Math.PI * i) / 22;
        const x = region.x + Math.cos(a) * region.rx;
        const y = region.y + Math.sin(a) * region.ry;
        ctx.strokeStyle = i % 2 ? "#ffffff0d" : "#0a293c55";
        ctx.lineWidth = 4 + hashNoise(i + region.x) * 12;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - 18, y + 85);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.ellipse(region.x, region.y, region.rx, region.ry, 0, 0, Math.PI * 2);
      ctx.fillStyle = ember ? "#b39057" : "#548e73";
      ctx.fill();
      ctx.save();
      ctx.clip();
      if (terrain.complete && terrain.naturalWidth > 0) {
        const half = terrain.naturalWidth / 2;
        ctx.drawImage(
          terrain,
          ember ? half : 0,
          0,
          half,
          terrain.naturalHeight,
          region.x - region.rx,
          region.y - region.ry,
          region.rx * 2,
          region.ry * 2,
        );
      }
      const shade = ctx.createRadialGradient(
        region.x,
        region.y,
        region.rx * 0.25,
        region.x,
        region.y,
        region.rx,
      );
      shade.addColorStop(0, "#00000000");
      shade.addColorStop(1, "#072e394c");
      ctx.fillStyle = shade;
      ctx.fillRect(
        region.x - region.rx,
        region.y - region.ry,
        region.rx * 2,
        region.ry * 2,
      );
      ctx.restore();
      ctx.strokeStyle = ember ? "#e6bb7866" : "#8ed4ad66";
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.restore();
    }
    // 两处连接只画在真实可走的接壤带，避免美术桥梁引导玩家走进空气。
    for (const x of [350, 1250]) {
      ctx.save();
      ctx.strokeStyle = ember ? "#c0a77f" : "#a3af92";
      ctx.lineWidth = 38;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(x - 100, 485);
      ctx.lineTo(x + 100, 485);
      ctx.stroke();
      ctx.strokeStyle = "#354c4966";
      ctx.lineWidth = 2;
      for (let step = -90; step <= 90; step += 20) {
        ctx.beginPath();
        ctx.moveTo(x + step, 468);
        ctx.lineTo(x + step - 4, 502);
        ctx.stroke();
      }
      ctx.restore();
    }
    for (let i = 0; i < 44; i++) {
      const x = -570 + hashNoise(i * 8.3) * 2740;
      if (Math.abs(x - camera.x) > 1000) continue;
      const y = 290 + hashNoise(i * 7.1) * 350;
      const phase = (time * 0.22 + i * 0.618) % 1;
      ctx.save();
      ctx.globalAlpha = Math.sin(phase * Math.PI) * 0.58;
      ctx.fillStyle = accent;
      ctx.beginPath();
      ctx.arc(
        x + Math.sin(time + i) * 6,
        y - phase * 48,
        1.5 + (i % 2),
        0,
        Math.PI * 2,
      );
      ctx.fill();
      ctx.restore();
    }
  }

  landmarks(
    ctx: CanvasRenderingContext2D,
    run: NonNullable<World["run"]>,
    time: number,
    destination: Vec2 | null,
  ): void {
    for (const [index, objective] of run.objectives.entries()) {
      const ready = objective.complete;
      const color = ready ? "#a7f4cf" : "#f7dba5";
      ctx.save();
      ctx.translate(objective.pos.x, objective.pos.y);
      ctx.fillStyle = "#0a29294d";
      ctx.beginPath();
      ctx.ellipse(0, 8, 45, 17, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#4c6761";
      ctx.strokeStyle = "#b7c7a2";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-28, 3);
      ctx.lineTo(-19, -58);
      ctx.lineTo(0, -79);
      ctx.lineTo(21, -58);
      ctx.lineTo(29, 3);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.shadowColor = color;
      ctx.shadowBlur = ready ? 24 : 10;
      const frame = Math.floor(time * 10) % 16;
      const glow = 0.65 + Math.sin((frame * Math.PI) / 8) * 0.2;
      ctx.globalAlpha = glow;
      ctx.beginPath();
      ctx.moveTo(0, -61);
      ctx.lineTo(12, -42);
      ctx.lineTo(0, -23);
      ctx.lineTo(-12, -42);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(0, 7, 38, 14, 0, 0, Math.PI * 2);
      ctx.stroke();
      this.label(
        ctx,
        `${ready ? "✓" : index + 1} ${objective.name}`,
        0,
        -96,
        color,
      );
      this.label(ctx, ready ? "已净化" : "点击调查", 0, 30, "#fff0d6", 12);
      ctx.restore();
    }
    ctx.save();
    ctx.translate(run.exit.x, run.exit.y);
    const rotation = (Math.floor(time * 12) / 12) * 0.65;
    ctx.fillStyle = "#86e8d626";
    ctx.beginPath();
    ctx.ellipse(0, 0, 47, 22, 0, 0, Math.PI * 2);
    ctx.fill();
    for (let i = 0; i < 3; i++) {
      ctx.strokeStyle = ["#bcffe2", "#74dfd2", "#c6e5ff"][i];
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.ellipse(
        0,
        -27,
        29 + i * 7,
        41 + i * 5,
        Math.sin(rotation + i) * 0.2,
        rotation + i,
        rotation + i + Math.PI * 1.55,
      );
      ctx.stroke();
    }
    this.label(
      ctx,
      run.phase === "cleared" ? "领取战利品 · 返回庭园" : "归途裂隙 · 撤离",
      0,
      -100,
      "#c5ffe7",
    );
    this.label(ctx, "点击传送 / E 交互", 0, 40, "#c5e6da", 12);
    ctx.restore();
    if (destination) {
      ctx.save();
      ctx.strokeStyle = "#fbe4a7";
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 6]);
      ctx.beginPath();
      ctx.ellipse(destination.x, destination.y, 22, 12, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  boss(
    ctx: CanvasRenderingContext2D,
    id: string,
    actor: Animated & { velocity?: Vec2 },
    time: number,
    facing: number,
    width = 250,
  ): boolean {
    let image = this.bosses.get(id);
    if (!image) {
      image = this.load(`/art/boss-v5/${id}.webp`);
      this.bosses.set(id, image);
    }
    if (!image.complete || image.naturalWidth === 0) return false;
    const sample = animationFrame(actor, time);
    const row = {
      idle: 0,
      walk: 1,
      attack: 3,
      gather: 4,
      hurt: 5,
      death: 6,
      wind: 0,
      harvest: 6,
      plant: 4,
      water: 4,
      cast: 3,
      dash: 1,
    }[sample.clip];
    // Boss蓄力按实际威胁时长采样，不在长预警前半秒就把动作放完。
    const frame =
      actor.animation?.clip === "attack"
        ? Math.min(
            7,
            Math.floor((actor.animation.age / actor.animation.duration) * 8),
          )
        : sample.frame;
    ctx.save();
    if (Math.cos(facing) < 0) ctx.scale(-1, 1);
    ctx.drawImage(
      image,
      (frame % 8) * BOSS_CELL,
      (row + Math.floor(frame / 8)) * BOSS_CELL,
      BOSS_CELL,
      BOSS_CELL,
      -width / 2,
      (-width * BOSS_GROUND) / BOSS_CELL,
      width,
      width,
    );
    ctx.restore();
    return true;
  }

  bossAccent(
    ctx: CanvasRenderingContext2D,
    enemy: Enemy,
    time: number,
    compactCombat = false,
  ): void {
    const vulnerable = (enemy.recovery ?? 0) > 0;
    const color = vulnerable ? "#b8ffeb" : "#ffd79a";
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = vulnerable ? 3 : 1;
    ctx.globalAlpha = vulnerable ? 0.9 : 0.3;
    ctx.setLineDash([10, 8]);
    ctx.lineDashOffset = -time * 15;
    ctx.beginPath();
    ctx.ellipse(
      0,
      4,
      enemy.radius + 13,
      (enemy.radius + 13) * 0.47,
      0,
      0,
      Math.PI * 2,
    );
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
    if (vulnerable && !compactCombat)
      this.label(ctx, "破绽 · 全力反击", 0, -235, color);
    ctx.restore();
  }

  private label(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    color: string,
    size = 14,
  ): void {
    ctx.font = `600 ${size}px "Microsoft YaHei UI",sans-serif`;
    ctx.textAlign = "center";
    ctx.strokeStyle = "#142b2be6";
    ctx.lineWidth = 4;
    ctx.lineJoin = "round";
    ctx.strokeText(text, x, y);
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
  }
}
